<template>
  <div class="notification-list">
    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="flex flex-col items-center justify-center py-12"
    >
      <div
        class="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"
      ></div>
      <p class="mt-4 text-gray-500">Chargement des notifications...</p>
    </div>

    <!-- Error State -->
    <div
      v-else-if="hasError"
      class="flex flex-col items-center justify-center py-12 px-4"
    >
      <ImgComponent
        src="/images/error/network.svg"
        alt="Erreur de chargement"
        class="w-32 h-32 mb-4 opacity-50"
      />
      <h3 class="text-lg font-semibold text-red-600 mb-2">
        Erreur de chargement
      </h3>
      <p class="text-sm text-gray-500 text-center mb-4">
        {{
          error ||
          'Une erreur est survenue lors du chargement des notifications. Veuillez réessayer.'
        }}
      </p>
      <button
        @click="loadNotifications"
        class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
      >
        Réessayer
      </button>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center justify-center py-12 px-4"
    >
      <ImgComponent
        src="/images/empty/notifications.svg"
        alt="Aucune notification"
        class="w-32 h-32 mb-4 opacity-50"
      />
      <h3 class="text-lg font-semibold text-gray-700 mb-2">
        Aucune notification
      </h3>
      <p class="text-sm text-gray-500 text-center">
        Vous n'avez pas encore de notifications. Elles apparaîtront ici dès que
        vous en recevrez.
      </p>
    </div>

    <!-- Notifications Grouped by Date -->
    <div v-else class="notifications-container">
      <!-- Today -->
      <div v-if="groupedNotifications.today.length > 0" class="mb-4">
        <div
          class="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider"
        >
          Aujourd'hui
        </div>
        <NotificationItem
          v-for="notification in groupedNotifications.today"
          :key="notification.id"
          :notification="notification"
          @click="handleNotificationClick"
          @delete="handleNotificationDelete"
        />
      </div>

      <!-- Yesterday -->
      <div v-if="groupedNotifications.yesterday.length > 0" class="mb-4">
        <div
          class="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider"
        >
          Hier
        </div>
        <NotificationItem
          v-for="notification in groupedNotifications.yesterday"
          :key="notification.id"
          :notification="notification"
          @click="handleNotificationClick"
          @delete="handleNotificationDelete"
        />
      </div>

      <!-- Older -->
      <div v-if="groupedNotifications.older.length > 0" class="mb-4">
        <div
          class="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider"
        >
          Plus anciens
        </div>
        <NotificationItem
          v-for="notification in groupedNotifications.older"
          :key="notification.id"
          :notification="notification"
          @click="handleNotificationClick"
          @delete="handleNotificationDelete"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, watch } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import NotificationItem from './NotificationItem.vue';

const props = defineProps({
  filterType: {
    type: String,
    default: 'all'
  },
  filterReadStatus: {
    type: String,
    default: 'all'
  }
});

const emit = defineEmits(['notification-click', 'notification-delete']);

// Store state - accessed via global properties
const getInboxStore = () => {
  return window.$stores?.inbox;
};

// Group notifications by date - using store's getter
const groupedNotifications = computed(() => {
  const store = getInboxStore();
  return store?.groupedNotifications || { today: [], yesterday: [], older: [] };
});

// Loading state from store
const isLoading = computed(() => {
  const store = getInboxStore();
  return store?.isLoading || false;
});

// Error state from store
const hasError = computed(() => {
  const store = getInboxStore();
  return store?.hasError || false;
});

const error = computed(() => {
  const store = getInboxStore();
  return store?.error || null;
});

// Empty state
const isEmpty = computed(() => {
  const store = getInboxStore();
  const notifications = store?.notifications || [];
  return !notifications || notifications.length === 0;
});

// Load notifications
const loadNotifications = () => {
  bus.emit(constants.EVENTS.INBOX.NOTIFICATIONS_LOAD, {
    filters: {
      type: props.filterType,
      readStatus: props.filterReadStatus
    }
  });
};

// Handle notification click
const handleNotificationClick = (notification) => {
  // Mark as read
  if (!notification.read) {
    bus.emit(constants.EVENTS.INBOX.NOTIFICATION_MARK_READ, notification.id);
  }
  emit('notification-click', notification);
};

// Handle notification delete
const handleNotificationDelete = (notificationId) => {
  bus.emit(constants.EVENTS.INBOX.NOTIFICATION_DELETE, notificationId);
  emit('notification-delete', notificationId);
};

// Watch for filter changes
watch(
  () => [props.filterType, props.filterReadStatus],
  () => {
    bus.emit(constants.EVENTS.INBOX.NOTIFICATION_FILTER_CHANGE, {
      type: props.filterType,
      readStatus: props.filterReadStatus
    });
  },
  { deep: true }
);

// Load notifications on mount
onMounted(() => {
  loadNotifications();
});

defineExpose({
  loadNotifications,
  isEmpty
});
</script>

<style scoped>
.notification-list {
  background-color: white;
  min-height: 100vh;
}

.notifications-container {
  padding-bottom: 5rem;
}
</style>
